import React, { createContext, useState, useContext } from 'react';

type AuthContextValue = {
    loading: boolean;
    error: string | null;
    login: (username: string, password: string, orgPin: string, rememberMe?: boolean) => Promise<{ success: boolean; error?: string }>;
    clearError: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const login = async (username: string, password: string, orgPin: string, rememberMe = false) => {
        try {
            setLoading(true);
            setError(null);

            const formData = new FormData();
            formData.append('username', username);
            formData.append('password', password);
            formData.append('orgPin', orgPin);
            if (rememberMe) {
                formData.append('rememberMe', 'remember-me');
            }
            formData.append('login', 'Sign in');

            const response = await fetch('/login-legacy.php', {
                method: 'POST',
                credentials: 'same-origin',
                body: formData
            });

            if (!response.ok && response.status === 500) {
                return { success: false, error: 'A server error occurred. Please try again or contact support.' };
            }

            if (response.redirected) {
                window.location.href = response.url;
                return { success: true };
            }

            const text = await response.text();

            let errorMatch = text.match(/class="alert alert-danger"[^>]*>(.*?)<\/div>/);
            if (!errorMatch) {
                errorMatch = text.match(/<div[^>]*alert[^>]*>(.*?)<\/div>/);
            }

            const err = errorMatch ? errorMatch[1].trim() : 'Authentication failed';
            setError(err);
            return { success: false, error: err };
        } catch (e) {
            const err = 'Login request failed. Please try again.';
            setError(err);
            return { success: false, error: err };
        } finally {
            setLoading(false);
        }
    };

    const clearError = () => setError(null);

    const value: AuthContextValue = { loading, error, login, clearError };
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

